export interface QuizQuestion {
  id: string
  question: string
  options: string[]
  correctAnswer: number
  explanation: string
  category: string
}

export const SECURITY_QUIZ_POOL: QuizQuestion[] = [
  {
    id: 'sec-1',
    question: 'What should you do if you receive a suspicious email?',
    options: [
      'Click on links to verify',
      'Forward to IT security team',
      'Delete immediately without reporting',
      'Reply to ask for clarification',
    ],
    correctAnswer: 1,
    explanation: 'Always report suspicious emails to the IT security team for investigation.',
    category: 'phishing',
  },
  {
    id: 'sec-2',
    question: 'How often should you change your password?',
    options: [
      'Never, if it is strong',
      'Every 90 days',
      'Only when required by the system',
      'Every week',
    ],
    correctAnswer: 1,
    explanation: 'Regular password changes help maintain account security.',
    category: 'passwords',
  },
  {
    id: 'sec-3',
    question: 'What is the best practice for storing sensitive documents?',
    options: [
      'On your desktop for easy access',
      'In encrypted, authorized storage',
      'Email them to yourself',
      'USB drive for portability',
    ],
    correctAnswer: 1,
    explanation: 'Sensitive documents should always be stored in encrypted, authorized storage systems.',
    category: 'data-handling',
  },
  {
    id: 'sec-4',
    question: 'Who should you contact first during a security incident?',
    options: [
      'Your manager',
      'IT Security team',
      'HR department',
      'Wait and see if it resolves',
    ],
    correctAnswer: 1,
    explanation: 'IT Security team should be contacted immediately during any security incident.',
    category: 'incident-response',
  },
  {
    id: 'sec-5',
    question: 'What is multi-factor authentication?',
    options: [
      'Using multiple passwords',
      'Using two or more verification methods',
      'Logging in from multiple devices',
      'Having multiple user accounts',
    ],
    correctAnswer: 1,
    explanation: 'MFA requires two or more verification methods to access an account.',
    category: 'access-control',
  },
]

export const SYSTEM_QUIZ_POOL: QuizQuestion[] = [
  {
    id: 'sys-1',
    question: 'Where can you find your upcoming meetings?',
    options: [
      'Settings page',
      'Calendar view',
      'Reports section',
      'Admin panel',
    ],
    correctAnswer: 1,
    explanation: 'The Calendar view shows all your scheduled meetings and events.',
    category: 'dashboard',
  },
  {
    id: 'sys-2',
    question: 'How do you send a message to a teammate?',
    options: [
      'Through the messaging feature',
      'Via external email only',
      'Through the calendar',
      'Messages are not supported',
    ],
    correctAnswer: 0,
    explanation: 'The internal messaging feature allows direct communication with teammates.',
    category: 'messaging',
  },
  {
    id: 'sys-3',
    question: 'Where can you view team performance reports?',
    options: [
      'Calendar',
      'Messaging',
      'Reports section',
      'Settings',
    ],
    correctAnswer: 2,
    explanation: 'The Reports section contains all team and individual performance metrics.',
    category: 'reports',
  },
]

export function sampleQuestions(
  pool: QuizQuestion[],
  count: number
): QuizQuestion[] {
  const shuffled = [...pool].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, Math.min(count, pool.length))
}

export function calculateScore(
  answers: Record<string, number>,
  questions: QuizQuestion[]
): number {
  let correct = 0
  for (const q of questions) {
    if (answers[q.id] === q.correctAnswer) {
      correct++
    }
  }
  return Math.round((correct / questions.length) * 100)
}

export interface SecurityScenario {
  id: string
  title: string
  description: string
  options: {
    id: string
    text: string
    isCorrect: boolean
    feedback: string
  }[]
}

export const SECURITY_SCENARIOS: SecurityScenario[] = [
  {
    id: 'scenario-1',
    title: 'Suspicious Email',
    description: 'You receive an email from what appears to be IT support asking you to verify your password by clicking a link. The email address looks slightly different from the usual IT domain.',
    options: [
      { id: 'a', text: 'Click the link and verify your password', isCorrect: false, feedback: 'Never click suspicious links. This is a common phishing technique.' },
      { id: 'b', text: 'Forward to IT security and delete', isCorrect: true, feedback: 'Correct! Always report suspicious emails to your security team.' },
      { id: 'c', text: 'Ignore and delete the email', isCorrect: false, feedback: 'While deleting is safe, you should report suspicious emails so IT can warn others.' },
      { id: 'd', text: 'Reply asking for clarification', isCorrect: false, feedback: 'Never engage with suspicious emails - this confirms your email is active.' },
    ],
  },
  {
    id: 'scenario-2',
    title: 'USB Drive Found',
    description: 'You find a USB drive in the parking lot labeled "Salary Data 2024". What do you do?',
    options: [
      { id: 'a', text: 'Plug it into your work computer to find the owner', isCorrect: false, feedback: 'Never plug in unknown devices - they may contain malware.' },
      { id: 'b', text: 'Turn it in to IT security', isCorrect: true, feedback: 'Correct! Unknown devices should be handled by security professionals.' },
      { id: 'c', text: 'Leave it where you found it', isCorrect: false, feedback: 'Leaving it could allow someone else to be compromised.' },
      { id: 'd', text: 'Take it home to check on your personal computer', isCorrect: false, feedback: 'This could compromise your personal devices and data.' },
    ],
  },
]
